/**
 * Syntactic screen for regex lexicon entries (VE-24948 / U5, ADR-001 as amended
 * by review gate #3).
 *
 * THIS IS NO LONGER THE SAFETY GATE. It used to be, and it failed three times.
 * The safety gate is now `regexProbeClient.probeRegexPerformance`, which TIMES
 * the pattern against page-scale adversarial inputs in a disposable worker.
 * This module keeps only what is certain from syntax alone.
 *
 * WHY THE CHANGE. Gates #1-#3 each broke an inference-based screen. The last
 * version accepted `(\w)*` x10 at 133,679 ms on a 40-character input, and
 * `[0-Z]*\W*` x4 at 419,342 ms, while REJECTING `(?:ab)+` and a plain email
 * pattern. Both directions had one cause: deciding cost from shape needs a full
 * parse of a language the screen only approximated, so every gap in the parse
 * became an unsafe default one way and a false rejection the other. A
 * measurement needs no such parse. The ~110-line character-class overlap
 * lattice and rule R8 that stood on it are deleted rather than corrected for a
 * fourth time; the calibration behind them was also taken at 120 characters,
 * where a real page is thousands.
 *
 * WHAT SURVIVES, AND WHY EACH ONE IS CERTAIN.
 *   R7  length         — a character count.
 *   INVALID_SYNTAX     — `new RegExp` either throws or does not.
 *   R3  backreference  — a syntactic fact; also unsupported downstream.
 *   R4  lookaround     — a syntactic fact; also unsupported downstream.
 *   R5  repeat count   — reads the digits the author typed.
 *   R6  quantifier cap — counts quantifiers; exists for REVIEWABILITY, not
 *                        speed. A 200-character pattern with 30 repeats is
 *                        unreadable by the person who has to approve it.
 *
 * Every survivor is decidable from the characters the author typed. Nothing
 * here infers a running cost any more.
 *
 * WHAT IS GONE, AND WHY EACH REMOVAL WAS MEASURED FIRST.
 *   R8  — stood entirely on the deleted lattice.
 *   R2  — quantified alternation, documented as "deliberately blunt" and
 *         knowingly false-positive-heavy. It rejected `(?:CR|CV)+` and
 *         `(cat|dog)s?`. The dangerous form `(a|a)*z` is caught by the probe.
 *   R1  — nested repetition. It read as the one safe structural rule, but it
 *         refused two patterns the probe clocks as harmless: the canonical
 *         email `(?:[a-z0-9_.+-]+)@(?:[a-z0-9-]+\.)+[a-z]{2,}` (61 ms across
 *         ten page-scale inputs) and `(?<yr>\d{4})+` (0 ms) — both "nested" by
 *         its definition. Its safety value is redundant: `(a+)+z`, `((a+))+z`
 *         and `(a|a)*z` all hit the probe's deadline and are refused there. A
 *         rule that blocks provably-safe patterns and catches nothing the gate
 *         misses is a cost with no benefit.
 *
 * WHY A BLACKLIST AND NOT A SCORE (unchanged). U5-FR7 requires an inline error
 * saying the pattern is "too complex". An author can act on "nested repetition
 * is not allowed"; nobody can act on "complexity score 47". So every rejection
 * still maps to exactly one named rule with one message — including the probe's,
 * which reuses this table so a rejection reads the same wherever it came from.
 *
 * PURITY IS DELIBERATE. No store, no network, no config, and no timing — so it
 * is callable from the settings page, the CSV importer and a unit test with no
 * fixture. The probe is async and lives next door for exactly that reason.
 */

import {
  ENTRY_REGEX_MAX_LENGTH,
  REGEX_MAX_QUANTIFIERS,
  REGEX_MAX_REPEAT_COUNT,
  REGEX_PROBE_BUDGET_MS,
} from './limits';

/**
 * Rule ids are part of the contract, not an implementation detail: they appear
 * in i18n message keys and in support conversations, so a rejection can be
 * discussed by rule rather than by quoting prose back and forth.
 *
 * `P1`/`P2` are the probe's, not this scanner's — they are here so every reason
 * a term can be refused has one id and one message in one table.
 */
export type RegexScreenRuleId =
  | 'R3_BACKREFERENCE'
  | 'R4_LOOKAROUND'
  | 'R5_REPEAT_COUNT'
  | 'R6_QUANTIFIER_COUNT'
  | 'R7_LENGTH'
  | 'P1_MEASURED_TOO_SLOW'
  | 'P2_PROBE_UNAVAILABLE'
  | 'INVALID_SYNTAX';

export const REGEX_SCREEN_MESSAGES: Readonly<
  Record<RegexScreenRuleId, string>
> = {
  R3_BACKREFERENCE: 'Backreferences are not supported.',
  R4_LOOKAROUND: 'Look-ahead and look-behind are not supported.',
  R5_REPEAT_COUNT: `Repeat counts above ${REGEX_MAX_REPEAT_COUNT} are not allowed.`,
  R6_QUANTIFIER_COUNT: `This pattern is too complex — more than ${REGEX_MAX_QUANTIFIERS} repeats.`,
  R7_LENGTH: `Pattern is too long (maximum ${ENTRY_REGEX_MAX_LENGTH} characters).`,
  // Says what was measured, because that is the one thing the author can act
  // on: the pattern is not malformed, it is slow on ordinary page text.
  P1_MEASURED_TOO_SLOW: `This pattern is too slow to run on a document — it took longer than ${REGEX_PROBE_BUDGET_MS}ms on a single page of text. Make the repeats more specific, or split it into separate entries.`,
  // Fail-closed, and says so honestly rather than implying the pattern is bad.
  P2_PROBE_UNAVAILABLE:
    'This pattern could not be checked for speed, so it cannot be saved. Please try again.',
  // U5-FR11 fixes this string verbatim for the CSV path; the inline path uses
  // the same text so a term rejected by hand and the same term rejected in a
  // CSV do not appear to fail for different reasons.
  INVALID_SYNTAX: 'Invalid regex, please update and try your upload again',
};

/** The failing half of `RegexScreenResult`, so `failRegexScreen` can be used
 * where a message is required without a narrowing dance at every call site. */
export interface RegexScreenFailure {
  readonly ok: false;
  readonly ruleId: RegexScreenRuleId;
  readonly message: string;
}

export type RegexScreenResult = { readonly ok: true } | RegexScreenFailure;

export const failRegexScreen = (
  ruleId: RegexScreenRuleId
): RegexScreenFailure => ({
  ok: false,
  ruleId,
  message: REGEX_SCREEN_MESSAGES[ruleId],
});

const fail = failRegexScreen;

const OK: RegexScreenResult = { ok: true };

/**
 * Reads a quantifier starting at `i`, if there is one.
 *
 * Returns the index just past it plus the largest repeat count it names (or
 * `null` for `*` / `+` / `?`). `{` that is not followed by a valid count is a
 * LITERAL brace, not a quantifier — treating it as one would reject `\d{`
 * patterns that JavaScript accepts.
 */
function readQuantifier(
  source: string,
  i: number
): {
  next: number;
  maxCount: number | null;
} | null {
  const ch = source[i];
  if (ch === '*' || ch === '+' || ch === '?') {
    // A trailing `?` makes the quantifier lazy; it is not a second quantifier.
    const lazy = source[i + 1] === '?';
    return { next: i + (lazy ? 2 : 1), maxCount: null };
  }
  if (ch !== '{') {
    return null;
  }
  const close = source.indexOf('}', i + 1);
  if (close === -1) {
    return null;
  }
  const body = source.slice(i + 1, close);
  if (!/^\d+(,\d*)?$/.test(body)) {
    return null;
  }
  const counts = body
    .split(',')
    .filter((p) => p.length > 0)
    .map((p) => Number.parseInt(p, 10))
    .filter((n) => Number.isFinite(n));
  // `{5,}` is unbounded above, so report the stated lower bound — `{500,}` is
  // still caught by R5.
  const maxCount = counts.length > 0 ? Math.max(...counts) : null;
  const lazy = source[close + 1] === '?';
  return { next: close + (lazy ? 2 : 1), maxCount };
}

/**
 * Length of a group's prologue at `i` (which points at `(`), including the `(`.
 *
 * Getting this wrong is what made `(?:ab)+` a rejected pattern: the old scanner
 * consumed only the `(`, so the very next iteration read the `?` of `(?:` as a
 * quantifier — inflating the R6 count and marking the frame as repeating, so
 * every non-capturing or named group tripped R1. The email pattern
 * `(?:[a-z0-9_.+-]+)@(?:[a-z0-9-]+\.)+[a-z]{2,}` was refused for that reason
 * alone.
 */
function groupPrologueLength(source: string, i: number): number {
  if (source[i + 1] !== '?') {
    return 1;
  }
  const third = source[i + 2];
  // `(?:` non-capturing, `(?=` `(?!` lookahead, `(?<=` `(?<!` lookbehind.
  if (third === ':' || third === '=' || third === '!') {
    return 3;
  }
  if (third === '<') {
    const fourth = source[i + 3];
    if (fourth === '=' || fourth === '!') {
      return 4;
    }
    // `(?<name>` — a named group; consume through the `>`.
    const close = source.indexOf('>', i + 3);
    return close === -1 ? 2 : close - i + 1;
  }
  // Anything else after `(?` is a flag group like `(?i)`; skip the `?` at least.
  return 2;
}

/**
 * Screens a regex entry for the properties that are certain from its syntax.
 *
 * A pass here is NOT a statement that the pattern is fast — that is
 * `probeRegexPerformance`'s job, and callers must run it before saving. Order
 * matters for the author's experience: a 900-character pattern should be told it
 * is too long, not handed a syntax error from somewhere in the middle of it.
 */
export function validateRegexEntry(pattern: string): RegexScreenResult {
  // R7 — cheapest check, and the one that makes the rest cheap.
  if (pattern.length > ENTRY_REGEX_MAX_LENGTH) {
    return fail('R7_LENGTH');
  }

  // U5-FR6 — is it even a regex? Do this before structural rules so an invalid
  // pattern is not reported as "too complex", which would be misleading.
  try {
    new RegExp(pattern);
  } catch {
    return fail('INVALID_SYNTAX');
  }

  let quantifierCount = 0;
  let inCharClass = false;
  let i = 0;

  while (i < pattern.length) {
    const ch = pattern[i];

    if (ch === '\\') {
      const next = pattern[i + 1];
      // R3 — a numeric escape outside a character class is a backreference.
      // Inside a class `[\1]` is an octal escape, not a reference.
      if (!inCharClass && next !== undefined && /[1-9]/.test(next)) {
        return fail('R3_BACKREFERENCE');
      }
      // Named backreference `\k<name>`.
      if (!inCharClass && next === 'k' && pattern[i + 2] === '<') {
        return fail('R3_BACKREFERENCE');
      }
      // Consume the whole escape. Two is right for `\d` and `\n`; for `\xNN`
      // and `\uNNNN` the remaining characters are literals and skipping them
      // as such is harmless here, since no rule below reads them.
      i += 2;
      continue;
    }

    if (inCharClass) {
      if (ch === ']') {
        inCharClass = false;
      }
      i += 1;
      continue;
    }

    if (ch === '[') {
      inCharClass = true;
      i += 1;
      continue;
    }

    if (ch === '(') {
      // R4 — lookaround. Checked at the opening paren so the message names the
      // construct rather than a downstream symptom.
      if (pattern[i + 1] === '?') {
        const third = pattern[i + 2];
        if (third === '=' || third === '!') {
          return fail('R4_LOOKAROUND');
        }
        if (third === '<') {
          const fourth = pattern[i + 3];
          // `(?<name>` is a named group and is fine; `(?<=` / `(?<!` are not.
          if (fourth === '=' || fourth === '!') {
            return fail('R4_LOOKAROUND');
          }
        }
      }
      i += groupPrologueLength(pattern, i);
      continue;
    }

    if (ch === ')') {
      // A quantifier on the group counts toward R5 and R6 like any other.
      const quant = readQuantifier(pattern, i + 1);
      if (quant) {
        if (
          quant.maxCount !== null &&
          quant.maxCount > REGEX_MAX_REPEAT_COUNT
        ) {
          return fail('R5_REPEAT_COUNT');
        }
        quantifierCount += 1;
        i = quant.next;
      } else {
        i += 1;
      }
      continue;
    }

    const quant = readQuantifier(pattern, i);
    if (quant) {
      if (quant.maxCount !== null && quant.maxCount > REGEX_MAX_REPEAT_COUNT) {
        return fail('R5_REPEAT_COUNT');
      }
      quantifierCount += 1;
      i = quant.next;
      continue;
    }

    i += 1;
  }

  // R6 — checked last so a pattern that also breaks a specific rule gets the
  // specific message, which is more actionable than a generic complexity one.
  //
  // This is a REVIEWABILITY ceiling, not a performance one. It used to carry
  // some of the safety burden; the probe carries that now, and this stays
  // because a lexicon entry has to be approved by a person.
  if (quantifierCount > REGEX_MAX_QUANTIFIERS) {
    return fail('R6_QUANTIFIER_COUNT');
  }

  return OK;
}
